import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Checkbox, Tooltip } from '@material-ui/core';

import {
  Examiner,
  LessonLocation,
  SwimmingLessonProvider,
} from '../../../types';

import Loader from '../../../components/Loader';
import DeactivateLessonLocationButton from './DeactivateLessonLocationButton';
import SLPExaminerSelector from './SLPExaminerSelector';
import AppContext from '../../../AppContext';
import SimpleTable from '../../../components/SimpleTable';
import SwimmingLessonProviderRepository from '../repository/SwimmingLessonProviderRepository';
import { Column } from '../../../components/DataTable';
import { formatAddress, formatDate } from '../../../utils/common';

interface SLPLessonLocationListProps {
  swimmingLessonProvider: SwimmingLessonProvider;
  isAllowedToEdit: boolean;
}

const SLPLessonLocationList = (props: SLPLessonLocationListProps) => {
  const { swimmingLessonProvider, isAllowedToEdit } = props;

  const history = useHistory();
  const notifications = useSnackbar();
  const { roleViewManager } = useContext(AppContext);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [lessonLocations, setLessonLocations] = useState<
    LessonLocation[] | null
  >(null);

  const repository = new SwimmingLessonProviderRepository();

  const canLinkExaminers =
    !swimmingLessonProvider.centralExams && isAllowedToEdit;

  const loadLessonLocations = useCallback(() => {
    new SwimmingLessonProviderRepository()
      .getLessonLocations(swimmingLessonProvider.id)
      .then((response) => setLessonLocations(response.data));
  }, [swimmingLessonProvider.id]);

  const handleItemSelect = (ids: string[]) => {
    setSelectedIds(ids);
  };

  const handleAttention = (
    event: ChangeEvent<HTMLInputElement>,
    lessonLocationId: string,
  ) => {
    repository
      .updateAttention(
        swimmingLessonProvider.id,
        lessonLocationId,
        event.target.checked,
      )
      .then(() => {
        setLessonLocations((prev) => {
          const index = prev?.findIndex(
            (location) => location.id === lessonLocationId,
          );

          if (prev === null || index === undefined || index === -1) {
            return prev;
          }

          return [
            ...prev.slice(0, index),
            { ...prev[index], attention: !prev[index].attention },
            ...prev.slice(index + 1, prev.length),
          ];
        });
        notifications.enqueueSnackbar(
          "De indicatie 'extra aandacht' is succesvol aangepast",
          {
            variant: 'success',
          },
        );
      })
      .catch(() => {
        notifications.enqueueSnackbar(
          "Er is iets fout gegaan bij aanpassen van 'extra aandacht'.",
          {
            variant: 'error',
          },
        );
      });
  };

  const handleSubmit = (examiner: Examiner) => {
    repository
      .linkExaminerToLessonLocations(
        swimmingLessonProvider.id,
        examiner.id,
        selectedIds,
      )
      .then(() => {
        notifications.enqueueSnackbar(
          'De examinator is succesvol gekoppeld aan de leslocaties!',
          {
            variant: 'success',
          },
        );
        history.go(0);
      })
      .catch(() => {
        notifications.enqueueSnackbar(
          'Er is iets fout gegaan bij het koppelen van de examinator aan de leslocaties.',
          {
            variant: 'error',
          },
        );
      });
  };

  const columns: Column[] = [
    {
      name: 'Naam',
      field: 'name',
      render: (lessonLocation: LessonLocation) => lessonLocation.name,
    },
    {
      name: 'E-mail',
      field: 'email',
      render: (lessonLocation: LessonLocation) => lessonLocation.email,
    },
    {
      name: 'Telefoon',
      field: 'telephone',
      render: (lessonLocation: LessonLocation) => lessonLocation.telephone,
    },
    {
      name: 'Adres',
      field: 'visitingAddress',
      render: (lessonLocation: LessonLocation) =>
        formatAddress(lessonLocation.visitingAddress),
    },
    {
      name: 'Licenties',
      field: 'licenses',
      render: (lessonLocation: LessonLocation) => {
        const licenses = [];
        if (lessonLocation.licenseSwimAbc) {
          licenses.push('zwem-ABC');
        }
        if (lessonLocation.licenseSnorkeling) {
          licenses.push('snorkelen');
        }
        if (lessonLocation.licenseSurvival) {
          licenses.push('survival');
        }
        if (lessonLocation.licenseAptitude) {
          licenses.push('zwemvaardigheid');
        }
        return licenses.join(', ');
      },
    },
  ];

  if (roleViewManager.isAdminView()) {
    columns.push({
      name: 'Laatste bezoek',
      field: 'mostRecentVisit',
      render: (lessonLocation: LessonLocation) =>
        lessonLocation?.mostRecentVisitDate
          ? formatDate(new Date(lessonLocation.mostRecentVisitDate))
          : null,
    });

    columns.push({
      name: 'Extra aandacht',
      field: 'attention',
      render: (lessonLocation: LessonLocation) => (
        <Tooltip
          title={
            lessonLocation.attention
              ? 'Geen extra aandacht meer'
              : 'Extra aandacht instellen'
          }
        >
          <Checkbox
            checked={lessonLocation.attention}
            onChange={(event) => handleAttention(event, lessonLocation.id)}
          />
        </Tooltip>
      ),
    });

    columns.push({
      name: '',
      field: 'deactivate',
      render: (lessonLocation: LessonLocation) => (
        <Tooltip title="Deactiveren">
          <DeactivateLessonLocationButton
            swimmingLessonProvider={swimmingLessonProvider}
            lessonLocation={lessonLocation}
            onSuccess={() => loadLessonLocations()}
          />
        </Tooltip>
      ),
    });
  }

  useEffect(() => loadLessonLocations(), [loadLessonLocations]);

  if (!lessonLocations) {
    return <Loader inline />;
  }

  return (
    <>
      <SimpleTable
        items={lessonLocations}
        columns={columns}
        canSelectItems={canLinkExaminers}
        onItemSelect={handleItemSelect}
      />
      {canLinkExaminers && (
        <SLPExaminerSelector
          swimmingLessonProvider={swimmingLessonProvider}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
};

export default SLPLessonLocationList;
